import * as React from 'react';

// API
import apibridge from '../../apibridge/';

// UTILS
import { queryStringToObject } from '../../utils';
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useLocation, useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import PageName from '../../components/PageName';
import GlobalModal from '../../components/GlobalModal';
import LoginCreateModal from '../Login/LoginCreateModal';

// ICONS
// static assets are placed in: C:\work\childaware-website\src\Portal\ClientApp\public\assets
// when react is built they are copied to: C:\work\childaware-website\src\Portal\ClientApp\build\assets
// or if react is not rebuilt - then place the svg in both folders
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import { PaymentPlansPaymentPlan } from '../../api/models';
interface PlansTypes {}

export const Plans: React.FC<PlansTypes> = ({}) => {
	const history = useHistory();
	const location = useLocation();

	// Check if the user is logged in
	const isLoggedIn = useAppSelector(({ user }) => user.loggedIn);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [modalIsOpen, setModalIsOpen] = React.useState(false);

	// State for Selected Plan Id
	const [selectedPlanId, setSelectedPlanId] = React.useState<string>();

	// Set up Data state
	const [planData, setPlanData] = React.useState<PaymentPlansPaymentPlan[]>([]);

	const returnUrl = '/register-organisation';

	const previousUrl = '/plans';

	const registerOrganisation = (paymentPlanId: string | undefined) => {
		if (paymentPlanId) {
			if (isLoggedIn) {
				history.push({
					pathname: returnUrl,
					state: {
						paymentPlanId: paymentPlanId
					}
				});
			} else {
				setSelectedPlanId(paymentPlanId);

				setModalIsOpen(true);
			}
		}
	};

	const getPlans = async () => {
		setIsLoading(true);

		const response = await apibridge.getPlanTypes();

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result?.paymentPlans) {
			setPlanData(response.result.paymentPlans);
		}

		setIsLoading(false);
	};

	const checkLocationParams = () => {
		const locationParams = location.state;

		const queryParams = queryStringToObject();

		const params = { ...locationParams, ...queryParams };

		const planIds = planData.map((plan) => plan.id);

		const selectedPlanId =
			params.paymentPlanId !== undefined && planIds.includes(params.paymentPlanId) ? params.paymentPlanId : undefined;

		if (selectedPlanId && !isLoggedIn) {
			registerOrganisation(selectedPlanId);
		}
	};

	React.useEffect(() => {
		getPlans();
	}, []);

	React.useEffect(() => {
		if (planData?.length) {
			checkLocationParams();
		}
	}, [planData]);

	const goBack = () => {
		history.goBack();
	};

	return (
		<div className="page-standalone-layout plans-page">
			<Button className="btn-text-icon go-back" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
				Back
			</Button>

			<PageName
				title="Let's find your perfect plan..."
				text="Start accessing the resources you need to make your organisation child-safe. Each plan unlocks unlimited user profiles for staff, parents, children, volunteers and visitors."
			/>

			<div className="page-content">
				{!!formErrorMessage ? (
					<div className="form-validation-error">{formErrorMessage}</div>
				) : (
					<SwitchTransition mode="out-in">
						<CSSTransition
							key={`key_${isLoading}`}
							timeout={600}
							classNames="standalone-fade"
							mountOnEnter
							unmountOnExit
						>
							{isLoading ? (
								<LoadingSpinner show={isLoading} />
							) : (
								<>
									{planData && planData.length > 0 && (
										<div className="plans-wrapper row">
											{planData.map((plan) => (
												<div className="plan-summary-wrapper col-md-6 col-xl-3" key={plan.id}>
													<div className="plan-summary">
														{!!plan.icon && <img src={'/assets/' + plan.icon} className="icon" />}

														<h2 className="h3">{plan.name}</h2>

														<p className="intro">
															{!!plan.description && (
																<span dangerouslySetInnerHTML={{ __html: plan.description }}></span>
															)}
														</p>

														<p className="price">
															<em>${plan.costPerChild?.toFixed(2)}</em> <br />
															per child per year
														</p>

														{plan.status === 'Active' ? (
															<Button className="btn-primary" onClick={() => registerOrganisation(plan.id)}>
																Select plan
															</Button>
														) : (
															<Button disabled={true} className="btn-primary">
																Coming soon
															</Button>
														)}
													</div>
												</div>
											))}
										</div>
									)}
								</>
							)}
						</CSSTransition>
					</SwitchTransition>
				)}
			</div>

			<GlobalModal className="w-auto" useDefaultStructure={false} isOpen={modalIsOpen} setIsOpen={setModalIsOpen}>
				<LoginCreateModal previousUrl={previousUrl} returnUrl={returnUrl} paymentPlanId={selectedPlanId} />
			</GlobalModal>
		</div>
	);
};

export default Plans;
